import { useState, useEffect } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  UserPlus, PhoneCall, RotateCcw, MapPin, PhoneForwarded,
  Loader2, ExternalLink, Flag, Clock, PauseCircle, PlayCircle,
} from "lucide-react";

export const TYPE_META = {
  novo_perfil: { label: "Novo perfil", icon: UserPlus, color: "#22c55e" },
  novo_numero: { label: "Novo número", icon: PhoneCall, color: "#c8aa6e" },
  reapareceu: { label: "Reapareceu", icon: RotateCcw, color: "#3b82f6" },
  reativou: { label: "Reativou", icon: PlayCircle, color: "#22c55e" },
  desativado: { label: "Anúncio desativado", icon: PauseCircle, color: "#ef4444" },
  mudou_cidade: { label: "Mudou de cidade", icon: MapPin, color: "#a855f7" },
  mudou_telefone: { label: "Mudou de número", icon: PhoneForwarded, color: "#f97316" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-PT", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function detailText(e) {
  const d = e.details || {};
  if (e.type === "mudou_cidade" && d.de) return `${d.de} → ${d.para}`;
  if (e.type === "mudou_telefone" && d.de) return `${d.de} → ${d.para}`;
  if (e.type === "reapareceu" && d.dias_ausente != null) return `Ausente ${d.dias_ausente} dia(s)`;
  if (e.type === "reativou" && d.dias_inativo != null) return `Inativo ${d.dias_inativo} dia(s)`;
  if (e.type === "novo_numero" && e.phone) return e.phone;
  return null;
}

export default function TimelineDialog({ profile, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !profile) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/profiles/${profile.id}/timeline`);
        if (active) setData(res.data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, profile]);

  const events = (data?.events || []).slice().reverse(); // newest first
  const p = data?.profile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-lg" data-testid="timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-normal flex items-center gap-3">
            <img src={imgProxy(profile?.photo_url, 160)} alt="" loading="lazy" decoding="async" className="w-9 h-9 object-cover grayscale-[20%] bg-[#27272a]" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
            <div>
              <div className="text-base flex items-center gap-2">
                {profile?.name || "—"}
                <Clock className="w-3.5 h-3.5 text-[#c8aa6e]" />
              </div>
              <div className="text-xs text-[#71717a]">{profile?.source_name} · {profile?.city || "—"}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {p && (
          <div className="grid grid-cols-3 gap-2 text-center border-y border-[#27272a] py-2 mb-1">
            <div>
              <div className="label-tiny">Primeiro</div>
              <div className="text-xs text-[#e4e4e7]">{fmtDateTime(p.first_seen).split(",")[0]}</div>
            </div>
            <div>
              <div className="label-tiny">Visto por último</div>
              <div className="text-xs text-[#e4e4e7]">{fmtDateTime(p.last_seen).split(",")[0]}</div>
            </div>
            <div>
              <div className="label-tiny">Anúncios c/ nº</div>
              <div className="text-xs text-[#e4e4e7]">{p.phone_ad_count || 1}</div>
            </div>
          </div>
        )}

        <div className="max-h-80 overflow-y-auto pr-1">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#71717a] py-6 justify-center">
              <Loader2 className="w-4 h-4 spin" /> A carregar histórico…
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="text-sm text-[#71717a] py-6 text-center">Sem eventos registados.</div>
          )}
          {!loading && events.length > 0 && (
            <ol className="relative border-l border-[#3f3f46] ml-3 space-y-4 py-1">
              {events.map((e) => {
                const meta = TYPE_META[e.type] || { label: e.label, icon: Flag, color: "#a1a1aa" };
                const Icon = meta.icon;
                const dt = detailText(e);
                return (
                  <li key={e.id} className="ml-5" data-testid="timeline-event">
                    <span className="absolute -left-[9px] flex items-center justify-center w-[18px] h-[18px] rounded-full" style={{ background: "#09090b", border: `1px solid ${meta.color}` }}>
                      <Icon className="w-2.5 h-2.5" style={{ color: meta.color }} />
                    </span>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium" style={{ color: meta.color }}>{meta.label}</span>
                      <span className="text-[10px] text-[#71717a] whitespace-nowrap">{fmtDateTime(e.at)}</span>
                    </div>
                    {dt && <div className="text-xs text-[#a1a1aa] font-mono mt-0.5">{dt}</div>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {profile?.profile_url && (
          <a href={profile.profile_url} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-xs text-[#c8aa6e] hover:text-[#d4b982] border-t border-[#27272a] pt-3 mt-1">
            <ExternalLink className="w-3.5 h-3.5" /> Abrir anúncio original
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}
